import { useMemo } from 'react';
import { RecordCard, RecordLine, recordWeight } from './RecordCard';

/**
 * The timeline of records — My Life's main view, also used by the
 * organisation page for one organisation's records. A spine runs down the
 * middle carrying the years and, where a year has records in more than one
 * month, the months; records hang off it left and right in date order. A
 * routine record (a visit, a note, a letter — `recordWeight` 'line') is one
 * line with a View link; something that matters is a compact card. A
 * stretch of time with no records is a dashed length of the spine. On a
 * phone the spine sits at the left edge and everything hangs to its right.
 * Pending (still encrypted) records group under their coarse year and
 * report themselves via `onVisible` so the caller can decrypt what is on
 * screen first.
 */
const GAP_MONTHS_THRESHOLD = 3;
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function monthOf(dateStr) {
  const m = dateStr ? parseInt(String(dateStr).slice(5, 7), 10) : NaN;
  return isNaN(m) ? null : m;
}

/** Dated records first in date order, then the undated ones in the order given. */
export function sortWithinYear(records) {
  const dated = records.filter((r) => r.date).sort((a, b) => a.date.localeCompare(b.date));
  return [...dated, ...records.filter((r) => !r.date)];
}

/**
 * The spine's items for one year: a month mark before the first record of
 * each month when the year's records fall in more than one month, else just
 * the records. Records alternate sides, a card always taking the side the
 * alternation gives it.
 */
export function spineItems(records) {
  const sorted = sortWithinYear(records);
  const months = new Set(sorted.map((r) => monthOf(r.date)).filter(Boolean));
  const out = [];
  let lastMonth = null;
  let side = 'left';
  for (const r of sorted) {
    const m = monthOf(r.date);
    if (months.size > 1 && m && m !== lastMonth) {
      out.push({ kind: 'month', key: `m-${m}`, label: MONTH_NAMES[m - 1] });
      lastMonth = m;
    }
    out.push({ kind: 'record', key: r.id, record: r, side, weight: recordWeight(r) });
    side = side === 'left' ? 'right' : 'left';
  }
  return out;
}


function parseDate(s) {
  if (!s) return null;
  try {
    const [y, m, d] = s.split('-').map((p) => parseInt(p, 10));
    return new Date(y, (m || 1) - 1, d || 1);
  } catch {
    return null;
  }
}

function monthsBetween(a, b) {
  if (!a || !b) return 0;
  return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
}

function yearOf(dateStr) {
  if (!dateStr) return null;
  const y = dateStr.slice(0, 4);
  const n = parseInt(y, 10);
  return isNaN(n) ? null : n;
}

/** Year a record is grouped under: its real date once decrypted, else the
 *  coarse plaintext year from the index, else 'unlocking' (a pre-existing
 *  record on its first load after this feature shipped) or 'undated'. */
function groupYear(r) {
  const y = yearOf(r.date) || r.year;
  if (y) return String(y);
  return r.pending ? 'unlocking' : 'undated';
}

const NUMBER_WORDS = [
  'no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve',
];

/**
 * "Nine months with no records" for gaps up to a year, otherwise
 * "1 year 3 months with no records".
 */
export function gapLabel(months) {
  if (months <= 12) {
    const word = NUMBER_WORDS[months] || String(months);
    const capitalised = word.charAt(0).toUpperCase() + word.slice(1);
    return `${capitalised} month${months === 1 ? '' : 's'} with no records`;
  }
  const years = Math.floor(months / 12);
  const rest = months % 12;
  const yearPart = `${years} year${years === 1 ? '' : 's'}`;
  const monthPart = rest ? ` ${rest} month${rest === 1 ? '' : 's'}` : '';
  return `${yearPart}${monthPart} with no records`;
}



export const TimelineView = ({ records, showGaps, onSelect, onVisible }) => {
  // Standing/reference information (a bulk system export, a blank pro-forma)
  // isn't something that happened on a date — it belongs in the
  // organisation's "General information" section, not the chronological
  // timeline. See ByOrgView.
  const eventRecords = useMemo(() => records.filter((r) => r.is_event !== false), [records]);

  const { groups, gaps } = useMemo(() => {
    const byYear = {};
    for (const r of eventRecords) {
      const y = groupYear(r);
      byYear[y] = byYear[y] || [];
      byYear[y].push(r);
    }
    const SPECIAL = { unlocking: 1, undated: 2 };
    const years = Object.keys(byYear).sort((a, b) => {
      if (SPECIAL[a] || SPECIAL[b]) return (SPECIAL[a] || 0) - (SPECIAL[b] || 0);
      return parseInt(a, 10) - parseInt(b, 10);
    });

    // Gaps between consecutive dated records
    const dated = eventRecords.filter((r) => r.date).sort((a, b) => a.date.localeCompare(b.date));
    const gapList = [];
    for (let i = 1; i < dated.length; i++) {
      const a = parseDate(dated[i - 1].date);
      const b = parseDate(dated[i].date);
      const diff = monthsBetween(a, b);
      if (diff >= GAP_MONTHS_THRESHOLD) {
        gapList.push({
          afterId: dated[i - 1].id,
          months: diff,
          from: dated[i - 1].date,
          to: dated[i].date,
        });
      }
    }
    return { groups: years.map((y) => ({ year: y, records: byYear[y] })), gaps: gapList };
  }, [eventRecords]);

  const gapsById = useMemo(() => {
    const m = {};
    for (const g of gaps) m[g.afterId] = g;
    return m;
  }, [gaps]);

  return (
    <div className="pd-spine" data-testid="timeline-view">
      {groups.map((g) => (
        <section
          key={g.year}
          aria-label={
            g.year === 'undated' ? 'Undated records'
              : g.year === 'unlocking' ? 'Records still unlocking'
                : `Records from ${g.year}`
          }
          data-testid={`timeline-year-${g.year}`}
        >
          <SpineMark kind="year">
            {g.year === 'undated' ? 'Undated' : g.year === 'unlocking' ? 'Unlocking' : g.year}
          </SpineMark>
          {spineItems(g.records).map((item) =>
            item.kind === 'month' ? (
              <SpineMark key={item.key} kind="month">{item.label}</SpineMark>
            ) : (
              <div key={item.key}>
                <SpineRow
                  record={item.record}
                  side={item.side}
                  weight={item.weight}
                  groupYear={/^\d{4}$/.test(g.year) ? g.year : null}
                  onSelect={onSelect}
                  onVisible={onVisible}
                />
                {showGaps && gapsById[item.record.id] && (
                  <GapIndicator months={gapsById[item.record.id].months} from={gapsById[item.record.id].from} to={gapsById[item.record.id].to} />
                )}
              </div>
            )
          )}
        </section>
      ))}
    </div>
  );
};

/** A year numeral or a month name sitting on the spine itself. */
const SpineMark = ({ kind, children }) => (
  <div className={`flex sm:justify-center ${kind === 'year' ? 'pt-7 pb-2' : 'pt-2 pb-1'}`}>
    {kind === 'year' ? (
      <h2 className="font-heading text-[30px] sm:text-[36px] font-bold tracking-[-0.035em] leading-none text-quiet bg-surface px-3 sm:px-4 tabular-nums">
        {children}
      </h2>
    ) : (
      <span className="pd-mono text-ink-55 bg-surface border border-rule rounded-full px-2.5 py-1 ml-[44px] sm:ml-0" data-testid="timeline-month">
        {children}
      </span>
    )}
  </div>
);

/**
 * One record on the spine: a dot on the line and the record to one side —
 * left or right from a tablet up, always to the right on a phone.
 */
const SpineRow = ({ record, side, weight, groupYear, onSelect, onVisible }) => {
  const sensitive = !!record.sensitive;
  const dot = sensitive ? 'pd-spine-dot pd-spine-dot-care' : weight === 'card' ? 'pd-spine-dot pd-spine-dot-sig' : 'pd-spine-dot';
  const body = weight === 'line' ? (
    <RecordLine record={record} onClick={() => onSelect(record.id)} onVisible={onVisible} groupYear={groupYear} align={side} />
  ) : (
    <RecordCard record={record} onClick={() => onSelect(record.id)} onVisible={onVisible} compact groupYear={groupYear} />
  );
  return (
    <div
      className="grid grid-cols-[44px_minmax(0,1fr)] sm:grid-cols-[minmax(0,1fr)_72px_minmax(0,1fr)]"
      data-testid={`timeline-row-${record.id}`}
      data-side={side}
    >
      <div className="col-start-1 sm:col-start-2 row-start-1 flex justify-center pt-[18px]" aria-hidden="true">
        <span className={dot} />
      </div>
      <div
        className={`row-start-1 min-w-0 py-1.5 col-start-2 pl-2 ${
          side === 'left' ? 'sm:col-start-1 sm:pl-0 sm:pr-2.5 sm:flex sm:justify-end' : 'sm:col-start-3 sm:pl-2.5'
        }`}
      >
        <div className="w-full sm:max-w-[460px]">{body}</div>
      </div>
    </div>
  );
};

const GapIndicator = ({ months, from, to }) => (
  <div
    className="pd-spine-gap flex sm:justify-center items-center py-5"
    role="note"
    aria-label={`${gapLabel(months)}, from ${from} to ${to}`}
    data-testid="timeline-gap"
  >
    <span className="relative text-[15px] text-ink-55 bg-surface px-3 ml-[44px] sm:ml-0" title={`${from} to ${to}`}>
      {gapLabel(months)}
    </span>
  </div>
);
